/* eslint-disable max-lines-per-function */
import { useMutation, useQuery } from "@apollo/client";
import css from "classnames";
import React from "react";
import { Viewer } from "components/auth/types";
import { QUERY_SIDEBAR_FILTERS } from "pages/dashboard/interests/interests-sidebar/query";
import { PropertyStageSettings, ToggleStageSettings } from "types/gql-types";
import { useSnackbar } from "components/haven-design/snackbar";
import { QUERY_STAGE_SETTINGS, TOGGLE_STAGE_SETTINGS } from "./query";
import styles from "./settings.module.css";

interface Props {
  path: string;
  propertyId: string;
  listId: string;
  viewer: Viewer;
}

const Settings: React.FC<Props> = (props) => {
  const [toggle, result] = useMutation<ToggleStageSettings>(TOGGLE_STAGE_SETTINGS, {
    refetchQueries: [{ query: QUERY_SIDEBAR_FILTERS, variables: { interestListId: props.listId } }],
  });
  const query = useQuery<PropertyStageSettings>(QUERY_STAGE_SETTINGS, {
    variables: { id: props.propertyId },
    fetchPolicy: "network-only",
  });
  const { openSnackbar } = useSnackbar();

  if (query.error || query.loading || !query.data) return null;

  const {
    property: { stages },
  } = query.data;

  return (
    <>
      <h3 className={styles.title}>Status settings</h3>
      <div className={styles.description}>
        Check all the statuses you want to enable at your property. All users with access to this
        property will be able to use these.
      </div>
      <div>
        {stages
          .filter((stage) => !stage.standard)
          .map((stage) => (
            <div
              key={stage.id}
              className={css(styles.stage, { [styles.inactive]: !stage.enabled })}
            >
              <label className={styles.label}>
                <input
                  className={styles.toggle}
                  type="checkbox"
                  checked={stage.enabled}
                  disabled={query.loading || result.loading || !props.viewer.isSupervisor}
                  onChange={async () => {
                    const variables = {
                      property: props.propertyId,
                      stage: stage.name,
                      enabled: !stage.enabled,
                    };
                    const toggleResult = await toggle({ variables });
                    if (!toggleResult.data) return;

                    const {
                      togglePropertyStage: { isSuccess, reason },
                    } = toggleResult.data;

                    if (!isSuccess) {
                      openSnackbar({ message: reason, status: "error" });
                      return;
                    }

                    await query.refetch();
                  }}
                />
                <span className={styles.name}>{stage.name}</span>
              </label>
            </div>
          ))}
      </div>
    </>
  );
};

export default Settings;
